import * as React from 'react'
import { format } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { Search, HandCoins, Calendar, TrendingUp, FileText, Download, CheckCircle2 } from 'lucide-react'
import { getOrders, updateOrder } from '@/api/orders'
import type { Order } from '@/api/orders'
import { Button } from '@/components/ui/button'

type PaidFilter = 'all' | 'unpaid' | 'paid'

export default function VendorRebateReportPage() {
  const navigate = useNavigate()
  const [vendorOptions, setVendorOptions] = React.useState<string[]>([])
  const [selectedVendor, setSelectedVendor] = React.useState('')
  const [paidFilter, setPaidFilter] = React.useState<PaidFilter>('unpaid')
  const [dateFrom, setDateFrom] = React.useState(() => {
    const d = new Date()
    d.setDate(1)
    return format(d, 'yyyy-MM-dd')
  })
  const [dateTo, setDateTo] = React.useState(() => format(new Date(), 'yyyy-MM-dd'))
  const [orders, setOrders] = React.useState<Order[]>([])
  const [loading, setLoading] = React.useState(false)
  const [searched, setSearched] = React.useState(false)
  const [markingId, setMarkingId] = React.useState<string | null>(null)
  const [paymentFilter, setPaymentFilter] = React.useState('')
  const [paymentOptions, setPaymentOptions] = React.useState<string[]>([])

  React.useEffect(() => {
    const tenantStr = localStorage.getItem('tenant')
    if (tenantStr) {
      try {
        const t = JSON.parse(tenantStr)
        const vendors = t.form_config?.vendors || []
        setVendorOptions(vendors.map((v: any) => typeof v === 'string' ? v : v.name || '').filter(Boolean))
        const payments = t.form_config?.payment_methods || ['現金', '刷卡', '轉帳', '月結']
        setPaymentOptions(Array.isArray(payments) ? payments : [])
      } catch {}
    }
    handleSearch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearch = async () => {
    setLoading(true)
    try {
      const res = await getOrders({
        limit: 1000,
        date_from: dateFrom,
        date_to: dateTo,
      })
      const vendorOrders = res.data.filter(o => !!o.vendor_name)
      setOrders(vendorOrders)
      setSearched(true)
    } finally {
      setLoading(false)
    }
  }

  const handleMarkPaid = async (order: Order) => {
    setMarkingId(order.id)
    try {
      const updated = await updateOrder(order.id, { vendor_paid_at: new Date().toISOString() })
      setOrders(prev => prev.map(o => o.id === order.id ? { ...o, vendor_paid_at: updated.vendor_paid_at } : o))
    } finally {
      setMarkingId(null)
    }
  }

  const filteredOrders = orders.filter(o => {
    if (selectedVendor && o.vendor_name !== selectedVendor) return false
    if (paymentFilter && o.payment_method !== paymentFilter) return false
    const isPaid = !!o.vendor_paid_at
    if (paidFilter === 'unpaid' && isPaid) return false
    if (paidFilter === 'paid' && !isPaid) return false
    return true
  })

  const totalAmount = Math.round(filteredOrders.reduce((sum, o) => sum + (o.pricing?.total_amount || 0), 0))
  const totalRebate = Math.round(filteredOrders.reduce((sum, o) => sum + (o.pricing?.vendor_rebate || 0), 0))
  const unpaidRebate = Math.round(filteredOrders.filter(o => !o.vendor_paid_at).reduce((sum, o) => sum + (o.pricing?.vendor_rebate || 0), 0))
  const paidRebate = Math.round(filteredOrders.filter(o => !!o.vendor_paid_at).reduce((sum, o) => sum + (o.pricing?.vendor_rebate || 0), 0))

  const vendorGroups = React.useMemo(() => {
    const map: Record<string, { vendor: string; count: number; total: number; unpaid: number; paid: number }> = {}
    filteredOrders.forEach(o => {
      const key = o.vendor_name || '未指定'
      if (!map[key]) map[key] = { vendor: key, count: 0, total: 0, unpaid: 0, paid: 0 }
      const rebate = o.pricing?.vendor_rebate || 0
      map[key].count += 1
      map[key].total += rebate
      if (o.vendor_paid_at) map[key].paid += rebate
      else map[key].unpaid += rebate
    })
    return Object.values(map).sort((a, b) => b.unpaid - a.unpaid)
  }, [filteredOrders])

  const handleExportExcel = async () => {
    const { utils, writeFile } = await import('xlsx')
    const headers = ['#', '廠商', '日期時間', '上車地點', '下車地點', '付款方式', '金額', '回金', '回收日期']
    const rows = filteredOrders.map((order, idx) => [
      idx + 1,
      order.vendor_name || '',
      order.pickup_datetime ? format(new Date(order.pickup_datetime), 'MM/dd HH:mm') : '-',
      order.pickup_location || '',
      order.dropoff_location || '',
      order.payment_method || '',
      Math.round(order.pricing?.total_amount || 0),
      Math.round(order.pricing?.vendor_rebate || 0),
      order.vendor_paid_at ? format(new Date(order.vendor_paid_at), 'yyyy-MM-dd') : '未回款',
    ])
    const wsData = [
      [`廠商費用管理`, '', '', '', '', '', '', '', ''],
      [`期間：${dateFrom} ～ ${dateTo}`, '', '', '', '', '', '', '', ''],
      [],
      headers,
      ...rows,
      [],
      ['', '', '', '', '', '', '', '回金合計', totalRebate],
      ['', '', '', '', '', '', '', '未回款', unpaidRebate],
      ['', '', '', '', '', '', '', '已回款', paidRebate],
    ]
    const ws = utils.aoa_to_sheet(wsData)
    ws['!cols'] = [{ wch: 4 }, { wch: 14 }, { wch: 12 }, { wch: 24 }, { wch: 24 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 12 }]
    const wb = utils.book_new()
    utils.book_append_sheet(wb, ws, '廠商費用管理')
    writeFile(wb, `廠商費用管理_${dateFrom}_${dateTo}.xlsx`)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 dark:bg-orange-900/30">
          <HandCoins className="h-5 w-5 text-orange-600 dark:text-orange-400" />
        </div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">廠商費用管理</h1>
      </div>

      {/* 篩選條件 */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="min-w-[160px] space-y-1">
            <label className="text-xs font-medium text-gray-500">廠商</label>
            <select
              value={selectedVendor}
              onChange={e => setSelectedVendor(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-gray-800 px-3 text-sm shadow-sm outline-none cursor-pointer"
            >
              <option value="">全部廠商</option>
              {vendorOptions.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div className="min-w-[140px] space-y-1">
            <label className="text-xs font-medium text-gray-500">付款方式</label>
            <select
              value={paymentFilter}
              onChange={e => setPaymentFilter(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-gray-800 px-3 text-sm shadow-sm outline-none cursor-pointer"
            >
              <option value="">全部</option>
              {paymentOptions.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="min-w-[140px] space-y-1">
            <label className="text-xs font-medium text-gray-500">回款狀態</label>
            <select
              value={paidFilter}
              onChange={e => setPaidFilter(e.target.value as PaidFilter)}
              className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-gray-800 px-3 text-sm shadow-sm outline-none cursor-pointer"
            >
              <option value="all">全部</option>
              <option value="unpaid">未回款</option>
              <option value="paid">已回款</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500 flex items-center gap-1"><Calendar className="h-3 w-3" />快速選擇</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  const now = new Date()
                  const first = new Date(now.getFullYear(), now.getMonth() - 1, 1)
                  const last = new Date(now.getFullYear(), now.getMonth(), 0)
                  const fmt = (d: Date) => format(d, 'yyyy-MM-dd')
                  setDateFrom(fmt(first))
                  setDateTo(fmt(last))
                }}
                className="h-10 px-4 rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-700 text-sm font-semibold text-orange-700 dark:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900/40 transition-colors cursor-pointer"
              >
                上個月
              </button>
              <button
                type="button"
                onClick={() => {
                  const now = new Date()
                  const first = new Date(now.getFullYear(), now.getMonth(), 1)
                  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0)
                  const fmt = (d: Date) => format(d, 'yyyy-MM-dd')
                  setDateFrom(fmt(first))
                  setDateTo(fmt(last))
                }}
                className="h-10 px-4 rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-700 text-sm font-semibold text-orange-700 dark:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900/40 transition-colors cursor-pointer"
              >
                本月
              </button>
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500 flex items-center gap-1"><Calendar className="h-3 w-3" />開始日期</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="flex h-10 rounded-md border border-input bg-white dark:bg-gray-800 px-3 text-sm shadow-sm outline-none"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500 flex items-center gap-1"><Calendar className="h-3 w-3" />結束日期</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="flex h-10 rounded-md border border-input bg-white dark:bg-gray-800 px-3 text-sm shadow-sm outline-none"
            />
          </div>
          <Button
            onClick={handleSearch}
            disabled={loading}
            className="h-10 px-6 bg-orange-600 hover:bg-orange-700 text-white font-bold"
          >
            <Search className="h-4 w-4 mr-2" />
            {loading ? '查詢中...' : '查詢'}
          </Button>
        </div>
      </div>

      {/* 統計卡片 */}
      {searched && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><FileText className="h-4 w-4" />訂單數</div>
            <div className="text-2xl font-black text-gray-900 dark:text-white">{filteredOrders.length} 筆</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><TrendingUp className="h-4 w-4" />應收回金（未回款）</div>
            <div className="text-2xl font-black text-red-600 dark:text-red-400">${unpaidRebate.toLocaleString()}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><CheckCircle2 className="h-4 w-4" />已回款總額</div>
            <div className="text-2xl font-black text-green-600 dark:text-green-400">${paidRebate.toLocaleString()}</div>
          </div>
        </div>
      )}

      {/* 依廠商分組統計 */}
      {searched && vendorGroups.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <span className="font-bold text-gray-900 dark:text-white text-sm">廠商匯總</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">廠商</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">訂單數</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">回金總額</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-red-500 uppercase">未回款</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-green-500 uppercase">已回款</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {vendorGroups.map(g => (
                  <tr key={g.vendor} className="bg-white dark:bg-gray-800">
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{g.vendor}</td>
                    <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">{g.count}</td>
                    <td className="px-4 py-3 text-right font-bold text-gray-900 dark:text-white">${Math.round(g.total).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-bold text-red-600 dark:text-red-400">${Math.round(g.unpaid).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-bold text-green-600 dark:text-green-400">${Math.round(g.paid).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 訂單明細 */}
      {searched && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <span className="font-bold text-gray-900 dark:text-white text-sm">訂單明細</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{dateFrom} ～ {dateTo}</span>
              {filteredOrders.length > 0 && (
                <Button
                  onClick={handleExportExcel}
                  variant="outline"
                  className="h-8 px-3 text-xs border-green-300 text-green-700 hover:bg-green-50"
                >
                  <Download className="h-3 w-3 mr-1" />
                  Excel
                </Button>
              )}
            </div>
          </div>
          {filteredOrders.length === 0 ? (
            <div className="py-16 text-center text-gray-400">此條件無訂單資料</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">廠商</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">日期</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">上下車地點</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">付款方式</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">金額</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-orange-500 uppercase">回金</th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">回收日期</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {filteredOrders.map((order, idx) => (
                    <tr
                      key={order.id}
                      className={`${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-slate-50 dark:bg-gray-750'} hover:bg-orange-50/50 dark:hover:bg-orange-900/10 cursor-pointer`}
                      onClick={() => navigate(`/orders?editOrder=${order.id}`)}
                    >
                      <td className="px-3 py-3 text-gray-400 text-xs">{idx + 1}</td>
                      <td className="px-3 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">{order.vendor_name}</td>
                      <td className="px-3 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap text-xs">
                        {order.pickup_datetime ? format(new Date(order.pickup_datetime), 'MM/dd HH:mm') : '-'}
                      </td>
                      <td className="px-3 py-3 max-w-[200px]">
                        <div className="text-xs text-gray-700 dark:text-gray-300 truncate">{order.pickup_location}</div>
                        <div className="text-xs text-gray-400 truncate">→ {order.dropoff_location}</div>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{order.payment_method}</td>
                      <td className="px-3 py-3 text-right text-xs font-medium text-gray-900 dark:text-white">
                        ${Math.round(order.pricing?.total_amount || 0).toLocaleString()}
                      </td>
                      <td className="px-3 py-3 text-right font-black text-orange-600 dark:text-orange-400">
                        ${Math.round(order.pricing?.vendor_rebate || 0).toLocaleString()}
                      </td>
                      <td className="px-3 py-3 text-center" onClick={e => e.stopPropagation()}>
                        {order.vendor_paid_at ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            <CheckCircle2 className="h-3 w-3" />
                            {format(new Date(order.vendor_paid_at), 'MM/dd')}
                          </span>
                        ) : (
                          <button
                            type="button"
                            disabled={markingId === order.id}
                            onClick={() => handleMarkPaid(order)}
                            className="px-2 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors disabled:opacity-50 cursor-pointer"
                          >
                            {markingId === order.id ? '處理中...' : '標記已回款'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-orange-50 dark:bg-orange-900/20 border-t-2 border-orange-200 dark:border-orange-700">
                  <tr>
                    <td colSpan={5} className="px-3 py-3 font-bold text-gray-700 dark:text-gray-300">合計 {filteredOrders.length} 筆</td>
                    <td className="px-3 py-3 text-right font-black text-gray-900 dark:text-white text-base">
                      ${totalAmount.toLocaleString()}
                    </td>
                    <td className="px-3 py-3 text-right font-black text-orange-600 dark:text-orange-400 text-base">
                      ${totalRebate.toLocaleString()}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
